/**
 * Client for Service Data Enrichment's product routes (part-enrichment-service), for the Enriched
 * data tab on DOC → Sources → Data. The contract is cty-part-enrichment-service/docs/API.md,
 * sections "Products and warranty" and "Bulk lookups and imports (tenant admins)".
 *
 * The browser calls the service's own host with the signed-in user's DOC token. The service takes
 * the tenant from the token and reads and writes that tenant's SOR records; the /v1/admin/products*
 * routes need a tenant admin. GET /v1/admin/me answers every signed-in user, so DOC can decide
 * whether to show the tab.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTypedSelector } from '@circuitry-ai/doc-data';

/** The service's base URL; '' when the tab is off (not configured, or a production build). */
export function getEnrichmentUrl(): string {
  if (import.meta.env.CTY_MODE === 'production') return '';
  return (import.meta.env.CTY_ENRICHMENT_URL ?? '').trim().replace(/\/+$/, '');
}

/** The OEM hints the service takes; it detects the OEM itself without one. */
export const OEMS = ['hp', 'dell', 'lenovo', 'dynabook'] as const;
export type OemCode = typeof OEMS[number];

export type CheckStatus = 'success' | 'not_found' | 'needs_more_info' | 'oem_unknown' | 'unsupported' | 'error';
export type WarrantyStatus = 'active' | 'expired' | 'not_started' | 'unknown';

export interface EnrichmentMe {
  /** May use the stored products and uploads. */
  admin: boolean;
  /** The caller's tenant has a SOR tenant for products. */
  enabled: boolean;
  scope?: 'platform' | 'tenant' | null;
  sor_tenant?: string | null;
  tenant_id?: string | null;
  reason?: string | null;
}

export interface Oem {
  code: string | null;
  name: string | null;
}

export interface Product {
  external_id?: string | null;
  product_name: string | null;
  product_number: string | null;
  model_number?: string | null;
}

export interface Unit {
  external_id?: string | null;
  serial_number: string | null;
  ship_date: string | null;
}

export interface Coverage {
  external_id?: string | null;
  name: string | null;
  coverage_type: string | null;
  service_type: string | null;
  service_levels: string[];
  deliverables: string[];
  start_date: string | null;
  end_date: string | null;
  /** From the dates, today. */
  status: WarrantyStatus;
  /** The OEM's own word at the last check. */
  status_reported: string;
  days_remaining: number | null;
}

export interface Warranty {
  /** Active while any coverage is. */
  status: WarrantyStatus;
  start_date: string | null;
  end_date: string | null;
  /** Until the last active coverage ends. */
  days_remaining: number | null;
  coverages: Coverage[];
}

export interface CheckError {
  code: string | null;
  message: string | null;
}

export interface Check {
  checked_at: string | null;
  age_days: number | null;
  status: CheckStatus | null;
  /** The OEM page the agent read. */
  source_url: string | null;
  detection?: { oem: string | null; confidence: string | null; method: string | null; candidates: string[] };
  /** With needs_more_info: what to send (e.g. model_number). */
  missing_fields: string[];
  error: CheckError | null;
  duration_ms: number | null;
  requested_tenant: string | null;
  /** lookup, refresh, bulk_lookup or file_import. */
  origin: string | null;
  batch_id: string | null;
  external_id?: string | null;
}

/** A unit's product and warranty: the answer of a lookup, and of the stored-product detail. */
export interface ProductLookup {
  /** Normalized: upper case, no spaces or hyphens. */
  serial_number: string;
  /** The OEM knows the unit. */
  found: boolean;
  status: CheckStatus | null;
  served_from: 'cache' | 'agent';
  /** The agent failed now; this is the stored unit, and refresh_error says why. */
  stale: boolean;
  refresh_error: CheckError | null;
  oem: Oem | null;
  product: Product | null;
  unit: Unit | null;
  warranty: Warranty | null;
  check: Check | null;
  next_check_after: string | null;
}

export interface LookupRequest {
  serial_number: string;
  oem?: string | null;
  /** Ask the OEM even if SOR has a recent answer. */
  refresh?: boolean;
  /** The bulk lookup this row belongs to. */
  batch_id?: string | null;
  model_number?: string | null;
  product_number?: string | null;
}

export interface WarrantySummary {
  status: WarrantyStatus | null;
  start_date: string | null;
  end_date: string | null;
  days_remaining: number | null;
  coverage_count: number;
  /** Of them, active today; a service from before it was added leaves it out. */
  active_count?: number | null;
}

export interface ProductListItem {
  serial_number: string;
  /** A stored unit; false for a `warranty=attention` row (a serial checked but not stored). */
  found?: boolean;
  oem: Oem | null;
  product: Product | null;
  unit: Unit | null;
  warranty: WarrantySummary;
  check: Check | null;
  next_check_after: string | null;
}

export interface ProductPage {
  total: number;
  /** `total` is a lower bound. */
  total_capped?: boolean;
  items: ProductListItem[];
}

/** `warranty=` on the list: active, ending within `ending_soon_days`, expired, or checked and not stored. */
export type WarrantyFilter = 'active' | 'ending' | 'expired' | 'attention';

export interface ProductQuery {
  q?: string;
  limit?: number;
  offset?: number;
  warranty?: WarrantyFilter;
  /** An OEM code from `stats.oems`. */
  oem?: string;
}

/** Stored serials by warranty today. */
export interface UnitCounts {
  total: number;
  /** Any coverage ends today or later. */
  active: number;
  /** Has coverages, all ended. */
  expired: number;
  no_coverage: number;
  /** Active, and the last active coverage ends within `ending_soon_days`. */
  ending_soon: number;
}

/** Serials whose newest check is not a success and that have no stored unit. */
export interface AttentionCounts {
  total: number;
  /** not_found, oem_unknown and unsupported. */
  not_found: number;
  needs_more_info: number;
  /** error. */
  failed: number;
}

export interface StatsOem {
  code: string;
  name: string;
  serials: number;
}

export interface ProductStats {
  /** The SOR tenant counted. */
  tenant: string;
  /** Records per definition: brand, product, serial, coverage, entitlement, warranty_check, batch. */
  records: Record<string, number>;
  checks_by_status: Record<string, number>;
  /** Active / expired today, from the end dates. */
  entitlements_by_status: Record<string, number>;
  recheck_days: number;
  not_found_retry_days: number;
  // The keys below are newer than the rest; an older service leaves them out.
  units?: UnitCounts;
  ending_soon_days?: number;
  /** The active unit whose last coverage ends soonest. */
  next_ending?: { serial_number: string; end_date: string; oem: string | null } | null;
  attention?: AttentionCounts;
  /** The OEMs among stored serials, by name. */
  oems?: StatsOem[];
  /** Products (models) among stored serials. */
  products?: number;
}

/** One check of a serial, as GET /v1/admin/products/{serial}/checks lists it. */
export interface CheckRecord extends Check {
  requested_by?: string | null;
  next_check_after?: string | null;
  /** The agent's whole answer, with include_answer=true. */
  answer?: unknown;
}

export interface CheckPage {
  total: number;
  items: CheckRecord[];
}

/** One row of a bulk lookup file; `row` is its 1-based line in the file. */
export interface BulkRow {
  row: number;
  /** OEM code (hp, dell, lenovo, dynabook); null when the cell names no known OEM. */
  oem: string | null;
  oem_text: string | null;
  serial_number: string;
  reason?: string | null;
}

export interface BulkRows {
  /** To look up. */
  rows: BulkRow[];
  /** OEMs whose sites block the agent (Dell): enrich locally, then import. */
  blocked: BulkRow[];
  invalid: BulkRow[];
}

export interface Batch {
  id: string | null;
  kind: string | null;
  state: string | null;
  file_name: string | null;
  row_count: number | null;
  counts: Record<string, number> | null;
  submitted_by_name: string | null;
  started_at: string | null;
  finished_at: string | null;
  notes: string | null;
}

export interface BatchStart {
  kind: 'bulk_lookup' | 'file_import';
  file_name: string;
  file_sha256?: string | null;
  row_count: number;
}

export interface BatchCounts {
  found: number;
  not_found: number;
  failed: number;
  skipped: number;
  stored: number;
}

export type BatchOutcome = 'completed' | 'cancelled' | 'failed';

export interface ImportResult {
  index: number;
  serial_number: string | null;
  /** The check's status, or skipped / failed. */
  result: string;
  found?: boolean | null;
  coverages?: number | null;
  reason?: string | null;
}

export interface ImportResponse {
  batch: Batch | null;
  counts: Record<string, number>;
  results: ImportResult[];
}

/**
 * Thrown for a non-2xx answer. `message` is for the admin; `detail` the API's own text (FastAPI's
 * `detail`); status 0 means not reached (or, for a lookup, no answer in time).
 */
export class EnrichmentApiError extends Error {
  constructor(message: string, readonly status: number, readonly detail: string | null = null) {
    super(message);
    this.name = 'EnrichmentApiError';
  }
}

export const UNREACHABLE = 'The enrichment service can’t be reached right now. Try again in a few minutes.';
export const TIMED_OUT = 'The OEM did not answer in time. Try again later.';

/** A lookup that misses waits for the OEM: ~20 s, at most 90 s on the service, plus SOR. */
export const LOOKUP_TIMEOUT_MS = 120_000;

/** `1h95-2613 qj` → `1H952613QJ`: how the service stores a serial number. */
export function normalizeSerial(text: string): string {
  return text.replace(/[\s-]/g, '').toUpperCase();
}

/**
 * Whether a search could be a serial number the lookup takes: letters, digits, `.`, `_`, `/`,
 * spaces and hyphens, 3–64 once spaces and hyphens are dropped (anything else answers 422).
 */
export function looksLikeSerial(text: string): boolean {
  const trimmed = text.trim();
  return /^[A-Za-z0-9._/ -]+$/.test(trimmed) && /^[A-Za-z0-9._/]{3,64}$/.test(normalizeSerial(trimmed));
}

/** FastAPI's `detail`: a string, or a list of validation errors. */
function detailOf(body: unknown): string | null {
  const detail = (body as { detail?: unknown } | null)?.detail;
  if (typeof detail === 'string' && detail) return detail;
  if (Array.isArray(detail)) {
    const messages = detail.map((item) => (typeof item?.msg === 'string' ? item.msg : '')).filter(Boolean);
    return messages.length > 0 ? messages.join('; ') : null;
  }
  return null;
}

function messageOf(status: number, detail: string | null): string {
  if (status === 403) return `Not allowed: ${detail ?? 'tenant admins only'}.`;
  if (status === 503) return `The product store is unavailable right now${detail ? ` (${detail})` : ''}. Try again in a few minutes.`;
  return detail ?? `HTTP ${status}`;
}

interface CallInit {
  method?: 'GET' | 'POST';
  body?: BodyInit;
  contentType?: string;
  signal?: AbortSignal;
  /** Gives up after this long (EnrichmentApiError TIMED_OUT); the caller's signal still aborts it. */
  timeoutMs?: number;
}

async function call<T>(baseUrl: string, path: string, token: string, init: CallInit = {}): Promise<T> {
  const { method = 'GET', body, contentType, signal, timeoutMs } = init;
  let timedOut = false;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let requestSignal = signal;
  let forward: (() => void) | undefined;
  if (timeoutMs) {
    const controller = new AbortController();
    forward = () => controller.abort();
    if (signal?.aborted) controller.abort();
    else signal?.addEventListener('abort', forward, { once: true });
    timer = setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, timeoutMs);
    requestSignal = controller.signal;
  }
  let response: Response;
  try {
    response = await fetch(`${baseUrl}${path}`, {
      method,
      body,
      signal: requestSignal,
      headers: { ...(contentType ? { 'Content-Type': contentType } : {}), Authorization: `Bearer ${token}` },
    });
  } catch (error) {
    if (timedOut) throw new EnrichmentApiError(TIMED_OUT, 0);
    if ((error as Error)?.name === 'AbortError') throw error;
    throw new EnrichmentApiError(UNREACHABLE, 0);
  } finally {
    clearTimeout(timer);
    if (forward) signal?.removeEventListener('abort', forward);
  }
  const answer = await response.json().catch(() => ({}));
  if (!response.ok) {
    const detail = detailOf(answer);
    throw new EnrichmentApiError(messageOf(response.status, detail), response.status, detail);
  }
  return answer as T;
}

/** The product routes, bound to one base URL and token. */
export function createEnrichmentClient(baseUrl: string, token: string) {
  const get = <T>(path: string, signal?: AbortSignal) => call<T>(baseUrl, path, token, { signal });
  const post = <T>(path: string, body: unknown, signal?: AbortSignal, timeoutMs?: number) =>
    call<T>(baseUrl, path, token, { method: 'POST', body: JSON.stringify(body), contentType: 'application/json', signal, timeoutMs });
  // An upload is the file's raw bytes; `file_name` tells the service what it is (.xlsx, .csv, .zip…).
  const upload = <T>(path: string, file: File, signal?: AbortSignal) =>
    call<T>(baseUrl, `${path}?file_name=${encodeURIComponent(file.name)}`, token, {
      method: 'POST', body: file, contentType: 'application/octet-stream', signal,
    });
  return {
    me: (signal?: AbortSignal) => get<EnrichmentMe>('/v1/admin/me', signal),
    /** A unit's product and warranty; a miss asks the OEM (~20 s), so it waits up to LOOKUP_TIMEOUT_MS. */
    lookup: (request: LookupRequest, signal?: AbortSignal) =>
      post<ProductLookup>('/v1/products/lookup', request, signal, LOOKUP_TIMEOUT_MS),
    products: (query: ProductQuery = {}, signal?: AbortSignal) => {
      const params = new URLSearchParams();
      for (const [key, value] of Object.entries(query)) {
        if (value !== undefined && value !== null && value !== '') params.set(key, String(value));
      }
      const search = params.toString();
      return get<ProductPage>(`/v1/admin/products${search ? `?${search}` : ''}`, signal);
    },
    /** What SOR holds for one serial (404 when it was never checked); the OEM is not asked. */
    product: (serialNumber: string, signal?: AbortSignal) =>
      get<ProductLookup>(`/v1/admin/products/${encodeURIComponent(serialNumber)}`, signal),
    stats: (signal?: AbortSignal) => get<ProductStats>('/v1/admin/products/stats', signal),
    /** Every check of a serial, newest first, whatever its outcome (404 when it was never checked). */
    checks: (serialNumber: string, options: { limit?: number; includeAnswer?: boolean } = {}, signal?: AbortSignal) => {
      const params = new URLSearchParams();
      if (options.limit) params.set('limit', String(options.limit));
      if (options.includeAnswer !== undefined) params.set('include_answer', String(options.includeAnswer));
      const search = params.toString();
      return get<CheckPage>(`/v1/admin/products/${encodeURIComponent(serialNumber)}/checks${search ? `?${search}` : ''}`, signal);
    },
    /** Reads a bulk lookup file (.xlsx or .csv); nothing is looked up. */
    bulkRows: (file: File, signal?: AbortSignal) => upload<BulkRows>('/v1/admin/products/bulk/rows', file, signal),
    startBatch: (batch: BatchStart, signal?: AbortSignal) => post<Batch>('/v1/admin/products/batches', batch, signal),
    /** A running batch's counts so far, so other admins see it move; the batch stays running. */
    batchProgress: (batchId: string, counts: BatchCounts, signal?: AbortSignal) =>
      post<Batch>(`/v1/admin/products/batches/${encodeURIComponent(batchId)}/progress`, { counts }, signal),
    finishBatch: (batchId: string, finish: { outcome: BatchOutcome; counts: BatchCounts }, signal?: AbortSignal) =>
      post<Batch>(`/v1/admin/products/batches/${encodeURIComponent(batchId)}/finish`, finish, signal),
    batches: (limit?: number, signal?: AbortSignal) =>
      get<Batch[]>(`/v1/admin/products/batches${limit ? `?limit=${limit}` : ''}`, signal),
    /** Stores the warranty agent's own answers (.json, .jsonl or .zip), in one batch. */
    importAnswers: (file: File, signal?: AbortSignal) => upload<ImportResponse>('/v1/admin/products/import', file, signal),
  };
}

export type EnrichmentClient = ReturnType<typeof createEnrichmentClient>;

/** The client for the signed-in user (null until there is a token). */
export function useEnrichmentClient(baseUrl: string): EnrichmentClient | null {
  const token = useTypedSelector((state) => state.auth.token) as string | null | undefined;
  return useMemo(() => (baseUrl && token ? createEnrichmentClient(baseUrl, token) : null), [baseUrl, token]);
}

/**
 * Whether the signed-in user may use the Enriched data tab: false while it is being asked, when the
 * service is not configured, and when /v1/admin/me fails. Asked once per page.
 */
export function useIsEnrichmentAdmin(baseUrl: string): boolean {
  const client = useEnrichmentClient(baseUrl);
  const [admin, setAdmin] = useState(false);
  const clientRef = useRef(client);
  clientRef.current = client;
  const ready = Boolean(client);
  useEffect(() => {
    const current = clientRef.current;
    if (!ready || !current) return undefined;
    const request = new AbortController();
    current.me(request.signal)
      .then((me) => { if (!request.signal.aborted) setAdmin(me?.admin === true); })
      .catch(() => { if (!request.signal.aborted) setAdmin(false); });
    return () => request.abort();
  }, [ready, baseUrl]);
  return ready && admin;
}
